import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../loader/Loader";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";

type FullScreenModalProps = {
  title: string;
  children: React.ReactNode;
  updateAsyncOperation: () => Promise<boolean>;
  closeFullScreenModal: CallableFunction;
  childErrorMessage?: string;
  isLoading?: boolean;
  closeModalAfterSubmit?: boolean;
  isUpdatePermission: boolean;
  isDeletePermission: boolean;
  confirmMessage: string;
  setShowSubmitButton?: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmitButton: boolean;
  showDeleteButton: boolean;
  deleteAsyncOperation?: () => Promise<boolean>;
  showEditButton?: boolean;
};

export interface ModalTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export const BootstrapModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export function BootstrapModalTitle(props: ModalTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        minHeight: "36px",
        m: 0,
        p: 1,
        backgroundColor: "primary.main",
        color: (theme) => theme.palette.grey[50],
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 0,
            color: (theme) => theme.palette.grey[50],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const FullScreenModal = (Props: FullScreenModalProps) => {
  const {
    title,
    children,
    updateAsyncOperation,
    closeFullScreenModal,
    childErrorMessage,
    isLoading = false,
    closeModalAfterSubmit = true,
    isUpdatePermission,
    isDeletePermission,
    confirmMessage,
    setShowSubmitButton,
    showSubmitButton,
    showDeleteButton,
    deleteAsyncOperation,
    showEditButton,
  } = Props;

  const [isOpen, setIsOpen] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [isOperationInProgress, setOperationInProgress] = useState(false);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }
    }
    return () => {
      isFunctionRan = false;
    };
  }, [childErrorMessage]);

  const _closeFullScreenModal = () => {
    setIsOpen(false);
    closeFullScreenModal();
  };

  const handleClickCloseModal = () => {
    _closeFullScreenModal();
  };

  const _executeOperation = async () => {
    if (childErrorMessage) {
      toast.error(childErrorMessage);
    }

    setOperationInProgress(true);
    try {
      const result = await updateAsyncOperation();
      setOperationInProgress(false);
      if (closeModalAfterSubmit && result) {
        _closeFullScreenModal();
      }
    } catch (e) {
      setOperationInProgress(false);
      toast.error(e!.toString());
    }
  };

  const handleEditButtonClick = () => {
    if (setShowSubmitButton) {
      setShowSubmitButton(true);
    }
  };

  const handleConfirmModalClose = (value: boolean) => {
    setConfirmModalOpen(false);
  };

  const handleConfirmModalOpen = () => {
    setConfirmModalOpen(true);
  };

  return (
    <BootstrapModal
      open={isOpen}
      onClose={handleClickCloseModal}
      aria-labelledby="customized-dialog-title"
      fullWidth
      fullScreen={true}
    >
      <BootstrapModalTitle
        id="customized-dialog-title"
        onClose={handleClickCloseModal}
      >
        <Typography>{title}</Typography>
      </BootstrapModalTitle>
      <DialogContent dividers>
        <div>{children}</div>
      </DialogContent>
      <DialogActions>
        {isUpdatePermission && showEditButton && !showSubmitButton && (
          <Button
            variant="contained"
            onClick={handleEditButtonClick}
            size="small"
          >
            Edit
          </Button>
        )}
        {isDeletePermission && showDeleteButton && !showSubmitButton && (
          <Button
            variant="contained"
            onClick={handleConfirmModalOpen}
            size="small"
            color="error"
          >
            Delete
          </Button>
        )}
        {showSubmitButton && (
          <Button
            variant="contained"
            onClick={() => _executeOperation()}
            size="small"
          >
            Submit
          </Button>
        )}
        <Button variant="outlined" onClick={handleClickCloseModal} size="small">
          Cancel
        </Button>
      </DialogActions>
      <Loader isLoading={isOperationInProgress || isLoading} />
      {isDeletePermission && (
        <ConfirmModal
          onClose={handleConfirmModalClose}
          asyncOperation={deleteAsyncOperation}
          open={confirmModalOpen}
          closeSubmitModal={_closeFullScreenModal}
          closeModalAfterSubmit={closeModalAfterSubmit}
          confirmMessage={confirmMessage}
        ></ConfirmModal>
      )}
    </BootstrapModal>
  );
};

export default FullScreenModal;
